<template>
  <div class="viewer-prefs" :class="tone">
    <div class="motion-prefs">
      <p class="pref-name">{{ t('settings.motion') }}</p>
      <p class="pref-hint">{{ t('settings.motionHint') }}</p>
      <div class="motion-seg">
        <button
          v-for="level in MOTION_LEVEL_ORDER"
          :key="level"
          type="button"
          class="motion-opt"
          :class="{ on: duelPrefs.motionLevel === level }"
          :aria-pressed="duelPrefs.motionLevel === level"
          @click="duelPrefs.setMotionLevel(level)"
        >
          {{ t(`settings.motionLevel.${level}`) }}
        </button>
      </div>
    </div>

    <div class="motion-prefs">
      <p class="pref-name">{{ t('settings.coinQuality') }}</p>
      <p class="pref-hint">{{ t('settings.coinQualityHint') }}</p>
      <div class="motion-seg">
        <button
          v-for="level in COIN_QUALITY_ORDER"
          :key="level"
          type="button"
          class="motion-opt"
          :class="{ on: duelPrefs.coinQuality === level }"
          :aria-pressed="duelPrefs.coinQuality === level"
          @click="duelPrefs.setCoinQuality(level)"
        >
          {{ t(`settings.coinQualityLevel.${level}`) }}
        </button>
      </div>
    </div>

    <div class="motion-prefs">
      <p class="pref-name">{{ t('settings.imageQuality') }}</p>
      <p class="pref-hint">{{ t('settings.imageQualityHint') }}</p>
      <p v-if="qualityChanged" class="pref-hint quality-apply">
        {{ t('settings.imageQualityApply') }}
      </p>
      <div class="motion-seg">
        <button
          v-for="level in BOARD_SHARPNESS_ORDER"
          :key="level"
          type="button"
          class="motion-opt"
          :class="{ on: duelPrefs.boardSharpness === level }"
          :aria-pressed="duelPrefs.boardSharpness === level"
          @click="duelPrefs.setBoardSharpness(level)"
        >
          {{ t(`settings.imageQualityLevel.${level}`) }}
        </button>
      </div>
    </div>

    <div class="motion-prefs">
      <p class="pref-name">{{ t('settings.foilSweep') }}</p>
      <p class="pref-hint">{{ t('settings.foilSweepHint') }}</p>
      <div class="motion-seg">
        <button
          v-for="mode in FOIL_SWEEP_ORDER"
          :key="mode"
          type="button"
          class="motion-opt"
          :class="{ on: duelPrefs.foilSweep === mode }"
          :aria-pressed="duelPrefs.foilSweep === mode"
          @click="duelPrefs.setFoilSweep(mode)"
        >
          {{ t(`settings.foilSweepMode.${mode}`) }}
        </button>
      </div>
    </div>

    <label
      class="pref-row"
      :class="{ off: duelPrefs.motionLevel === 'minimal' }"
    >
      <input
        type="checkbox"
        :checked="duelPrefs.animateFoilThumbs"
        :disabled="duelPrefs.motionLevel === 'minimal'"
        @change="onFoilThumbs"
      >
      <span>
        <span class="pref-name">{{ t('settings.foilThumbs') }}</span>
        <span class="pref-hint">{{ t('settings.foilThumbsHint') }}</span>
      </span>
    </label>

    <label class="pref-row">
      <input
        type="checkbox"
        :checked="duelPrefs.followOpponentViewer"
        @change="onFollow"
      >
      <span>
        <span class="pref-name">{{ t('settings.followOpponent') }}</span>
        <span class="pref-hint">{{ t('settings.followOpponentHint') }}</span>
      </span>
    </label>

    <label v-if="showZoom" class="pref-row">
      <input
        type="checkbox"
        :checked="duelPrefs.hideCardViewer"
        @change="onHideCardViewer"
      >
      <span>
        <span class="pref-name">{{ t('settings.hideCardViewer') }}</span>
        <span class="pref-hint">{{ t('settings.hideCardViewerHint') }}</span>
      </span>
    </label>

    <label v-if="showZoom" class="pref-row">
      <input
        type="checkbox"
        :checked="duelPrefs.focusBands"
        @change="onFocusBands"
      >
      <span>
        <span class="pref-name">{{ t('settings.focusBands') }}</span>
        <span class="pref-hint">{{ t('settings.focusBandsHint') }}</span>
      </span>
    </label>

    <ResponseModeChip layout="prefs" :tone="tone" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  BOARD_SHARPNESS_ORDER,
  COIN_QUALITY_ORDER,
  FOIL_SWEEP_ORDER,
  MOTION_LEVEL_ORDER,
  useDuelPrefsStore,
} from '../stores/duelPrefs'
import { useI18n } from '../i18n/t'
import ResponseModeChip from './ResponseModeChip.vue'

withDefaults(
  defineProps<{
    showZoom?: boolean
    tone?: 'duel' | 'menu'
  }>(),
  {
    showZoom: false,
    tone: 'duel',
  },
)

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()
const qualityOnOpen = duelPrefs.boardSharpness
const qualityChanged = computed(() => duelPrefs.boardSharpness !== qualityOnOpen)

function onFoilThumbs(event: Event): void {
  const el = event.target as HTMLInputElement
  duelPrefs.setAnimateFoilThumbs(el.checked)
}

function onFollow(event: Event): void {
  const el = event.target as HTMLInputElement
  duelPrefs.setFollowOpponentViewer(el.checked)
}

function onFocusBands(event: Event): void {
  const el = event.target as HTMLInputElement
  duelPrefs.setFocusBands(el.checked)
}

function onHideCardViewer(event: Event): void {
  const el = event.target as HTMLInputElement
  duelPrefs.setHideCardViewer(el.checked)
}
</script>

<style scoped>
.motion-prefs {
  margin: 0 0 14px;
}

.motion-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}

.motion-opt {
  flex: 1 1 4.5rem;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.65);
  color: #e2e8f0;
  font-family: Roboto, system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.motion-opt.on {
  background: rgba(201, 162, 39, 0.28);
  color: #fde68a;
  border-color: rgba(232, 197, 71, 0.45);
}

.menu .motion-prefs {
  margin-bottom: 16px;
}

.menu .motion-opt {
  border-color: var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface-variant);
}

.menu .motion-opt.on {
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
  border-color: transparent;
}

.pref-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 14px;
  cursor: pointer;
}

.pref-row.off {
  opacity: 0.5;
  cursor: default;
}

.pref-row input {
  margin-top: 3px;
  accent-color: var(--md-sys-color-primary, #c9a227);
}

.pref-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #f8fafc;
}

.pref-hint {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  line-height: 15px;
  color: #94a3b8;
  font-weight: 400;
}

.quality-apply {
  color: #fde68a;
  font-weight: 700;
}

.menu .pref-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface);
}

.menu .pref-hint {
  font-size: 13px;
  line-height: 18px;
  color: var(--md-sys-color-on-surface-variant);
}

.menu .quality-apply {
  color: var(--md-sys-color-primary, #e8c547);
}

.menu .pref-row {
  margin-bottom: 16px;
}

.viewer-prefs :deep(.mode-prefs) {
  margin: 0 0 14px;
}

.menu :deep(.mode-prefs) {
  margin-bottom: 16px;
}
</style>
